import { useState, useEffect, useRef } from 'react';
import { Search, X, Filter, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { api } from '@/lib/api';

const FilterSection = ({ id, title, isOpen, onToggle, children }) => (
  <Collapsible
    open={isOpen}
    onOpenChange={(open) => onToggle(id, open)}
    className="border-b border-border pb-4"
  >
    <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:text-primary transition-colors">
      <span className="text-sm font-semibold uppercase tracking-wider">{title}</span>
      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </CollapsibleTrigger>
    <CollapsibleContent className="pt-3 space-y-3">
      {children}
    </CollapsibleContent>
  </Collapsible>
);

export const FilterSidebar = ({
  filters,
  filterOptions,
  onFilterChange,
  onClearFilters,
  vehicleCount = 0
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [searchPreviewCount, setSearchPreviewCount] = useState(null);
  const [searchPreviewLoading, setSearchPreviewLoading] = useState(false);
  const debounceRef = useRef(null);
  const [openSections, setOpenSections] = useState({
    tipo: true,
    marca: true
  });

  useEffect(() => {
    setLocalFilters(filters);
    setSearchInput(filters.search || '');
  }, [filters]);

  // Debounced search preview count
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!searchInput || searchInput === (filters.search || '')) {
      setSearchPreviewCount(null);
      return;
    }

    setSearchPreviewLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const params = { ...localFilters, search: searchInput, status: 'publicado' };
        delete params.page;
        const data = await api.getVehiclesCount(params);
        setSearchPreviewCount(data.count);
      } catch {
        setSearchPreviewCount(null);
      } finally {
        setSearchPreviewLoading(false);
      }
    }, 500);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchInput]);

  const handleSectionToggle = (id, open) => {
    setOpenSections(prev => ({ ...prev, [id]: open }));
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleSearch = () => {
    const newFilters = { ...localFilters, search: searchInput || undefined };
    setLocalFilters(newFilters);
    setSearchPreviewCount(null);
    onFilterChange(newFilters);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleClear = () => {
    setLocalFilters({});
    setSearchInput('');
    setSearchPreviewCount(null);
    onClearFilters();
  };

  const hasActiveFilters = Object.values(localFilters).some(v => v !== undefined && v !== '' && v !== null) || searchInput;

  const filterContent = (
    <div className="space-y-4">
      {/* Search */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar vehículo..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyPress}
            className="pl-10 pr-20 bg-input border-transparent focus:border-primary"
            data-testid="filter-search"
          />
          <Button
            type="button"
            size="sm"
            onClick={handleSearch}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 px-3 bg-primary hover:bg-primary/90 text-white text-xs"
            data-testid="search-btn"
          >
            Buscar
          </Button>
        </div>
        {searchInput && searchInput !== (filters.search || '') && (
          <div className="flex items-center gap-1.5 text-xs px-1" data-testid="search-preview-count">
            {searchPreviewLoading ? (
              <><Loader2 className="w-3 h-3 animate-spin text-muted-foreground" /><span className="text-muted-foreground">Buscando...</span></>
            ) : searchPreviewCount !== null ? (
              <span className="text-primary font-medium">~{searchPreviewCount} resultados</span>
            ) : null}
          </div>
        )}
      </div>

      {/* Vehicle Count */}
      <div className="flex items-center justify-between py-2 border-b border-border">
        <span className="text-sm text-muted-foreground">Resultados</span>
        <span className="text-sm font-bold text-primary">{vehicleCount} vehiculos</span>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleClear}
          className="w-full bg-destructive/10 border-destructive text-white hover:bg-destructive"
          data-testid="clear-filters"
        >
          <X className="w-4 h-4 mr-2" />
          Limpiar Filtros
        </Button>
      )}

      {/* Type Filter */}
      <FilterSection id="tipo" title="Tipo" isOpen={openSections.tipo} onToggle={handleSectionToggle}>
        <Select
          value={localFilters.tipo || ''}
          onValueChange={(value) => handleFilterChange('tipo', value === 'all' ? undefined : value)}
        >
          <SelectTrigger className="bg-input border-transparent" data-testid="filter-tipo">
            <SelectValue placeholder="Todos los tipos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {filterOptions?.tipos?.map(tipo => (
              <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterSection>

      {/* Brand Filter */}
      <FilterSection id="marca" title="Marca" isOpen={openSections.marca} onToggle={handleSectionToggle}>
        <Select
          value={localFilters.marca || ''}
          onValueChange={(value) => handleFilterChange('marca', value === 'all' ? undefined : value)}
        >
          <SelectTrigger className="bg-input border-transparent" data-testid="filter-marca">
            <SelectValue placeholder="Todas las marcas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las marcas</SelectItem>
            {filterOptions?.marcas?.map(marca => (
              <SelectItem key={marca} value={marca}>{marca}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterSection>

    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-72 flex-shrink-0">
        <div className="sticky top-24 bg-card border border-border rounded-sm p-6 space-y-4">
          <div className="flex items-center gap-2 pb-4 border-b border-border">
            <Filter className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold uppercase tracking-wider">Filtros</h2>
          </div>
          {filterContent}
        </div>
      </aside>

      {/* Mobile Filter Button & Sheet */}
      <div className="lg:hidden fixed bottom-6 right-6 z-40">
        <Sheet>
          <SheetTrigger asChild>
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 text-white rounded-full shadow-lg h-14 px-5 gap-2"
              data-testid="mobile-filter-toggle"
            >
              <Filter className="w-5 h-5" />
              <span className="text-sm font-bold">Filtros</span>
              {hasActiveFilters && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-secondary rounded-full text-xs flex items-center justify-center">
                  !
                </span>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[320px] bg-card border-border overflow-y-auto">
            <SheetHeader className="pb-4 border-b border-border">
              <SheetTitle className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-primary" />
                <span className="text-lg font-bold uppercase tracking-wider">Filtros</span>
              </SheetTitle>
            </SheetHeader>
            <div className="py-6">
              {filterContent}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
};
